import { useState } from 'react';
import { X, LayoutTemplate } from 'lucide-react';
import { EMAIL_TEMPLATES, type EmailTemplate } from '../../lib/emailTemplates.ts';
import { cn } from './cn.ts';

// ─── Blank template thumbnail SVG ────────────────────────────────────────────

const BLANK_THUMBNAIL = `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 260" width="200" height="260">
    <rect width="200" height="260" fill="#f8f8f8" rx="4"/>
    <rect x="16" y="16" width="168" height="228" fill="white" rx="3" stroke="#e4e4e7" stroke-width="1.5" stroke-dasharray="6 3"/>
    <text x="100" y="120" text-anchor="middle" font-family="Arial" font-size="28" fill="#d4d4d8">+</text>
    <text x="100" y="148" text-anchor="middle" font-family="Arial" font-size="11" fill="#a1a1aa">Start blank</text>
  </svg>`,
)}`;

// ─── Template card ─────────────────────────────────────────────────────────────

function TemplateCard({
  template,
  selected,
  onSelect,
}: {
  template: { id: string; name: string; description: string; thumbnail: string } | null; // null = blank
  selected: boolean;
  onSelect: () => void;
}) {
  const isBlank = template === null;
  const thumb = isBlank ? BLANK_THUMBNAIL : template!.thumbnail;
  const name = isBlank ? 'Start blank' : template!.name;
  const desc = isBlank ? 'Empty canvas — build from scratch' : template!.description;

  return (
    <button
      onClick={onSelect}
      className={cn(
        'flex flex-col rounded-xl border-2 overflow-hidden text-left transition-all hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400',
        selected
          ? 'border-indigo-500 shadow-md shadow-indigo-100'
          : 'border-zinc-200 hover:border-zinc-300',
      )}
    >
      {/* Thumbnail */}
      <div className="relative bg-zinc-50 flex items-center justify-center overflow-hidden" style={{ height: 160 }}>
        <img
          src={thumb}
          alt={name}
          className="w-full h-full object-cover"
          style={{ imageRendering: 'crisp-edges' }}
        />
        {selected && (
          <div className="absolute inset-0 bg-indigo-500/10 flex items-center justify-center">
            <div className="w-7 h-7 rounded-full bg-indigo-500 flex items-center justify-center shadow-lg">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M2 7l3.5 3.5L12 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="px-3 py-2.5 bg-white flex-1">
        <p className="text-sm font-semibold text-zinc-900 leading-tight">{name}</p>
        <p className="text-xs text-zinc-400 mt-0.5 leading-snug">{desc}</p>
      </div>
    </button>
  );
}

// ─── Modal ─────────────────────────────────────────────────────────────────────

interface TemplatePickerModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with null for blank, or the EmailTemplate design JSON */
  onSelect: (design: Record<string, unknown> | null) => void;
}

export function TemplatePickerModal({ open, onClose, onSelect }: TemplatePickerModalProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!open) return null;

  const allOptions: Array<EmailTemplate | null> = [null, ...EMAIL_TEMPLATES];

  function handleConfirm() {
    if (selectedId === null) {
      // blank
      onSelect(null);
    } else {
      const tpl = EMAIL_TEMPLATES.find((t) => t.id === selectedId);
      onSelect(tpl?.design ?? null);
    }
    setSelectedId(null);
    onClose();
  }

  function handleSkip() {
    setSelectedId(null);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleSkip} />

      {/* Panel */}
      <div className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 flex flex-col overflow-hidden max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-indigo-50 rounded-lg">
              <LayoutTemplate size={16} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-zinc-900">Choose a starting template</h2>
              <p className="text-xs text-zinc-400">Pick one to get started faster, or begin from scratch</p>
            </div>
          </div>
          <button
            onClick={handleSkip}
            className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {allOptions.map((tpl) => {
              const id = tpl === null ? '__blank__' : tpl.id;
              return (
                <TemplateCard
                  key={id}
                  template={tpl}
                  selected={selectedId === (tpl === null ? '__blank__' : tpl.id)}
                  onSelect={() => setSelectedId(tpl === null ? '__blank__' : tpl.id)}
                />
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-100 bg-zinc-50 flex-shrink-0">
          <button
            onClick={handleSkip}
            className="text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
          >
            Skip for now
          </button>
          <button
            onClick={handleConfirm}
            disabled={selectedId === null}
            className={cn(
              'px-5 py-2 text-sm font-medium rounded-lg transition-colors',
              selectedId !== null
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-zinc-100 text-zinc-300 cursor-not-allowed',
            )}
          >
            Use this template →
          </button>
        </div>
      </div>
    </div>
  );
}
